import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/DraftPlayers.css';

function DraftPlayers() {
  const { id: leagueId } = useParams();
  const { token, user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const tours = sportConfig?.tours || [];
  const [league, setLeague] = useState(null);
  const [available, setAvailable] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [heroPool, setHeroPool] = useState([]);
  const [myHeroPick, setMyHeroPick] = useState(null);
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroError, setHeroError] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [nameSearch, setNameSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(null);
  const [dropping, setDropping] = useState(null); // pickId being dropped
  const [dropConfirm, setDropConfirm] = useState(null); // { pickId, playerName, costCredits }
  const [error, setError] = useState(null);
  const [draftError, setDraftError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchAll();
  }, [leagueId]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchLeague(), fetchAvailable(), fetchMyPicks(), fetchHeroData()]);
    setLoading(false);
  };

  const fetchLeague = async () => {
    try {
      const res = await fetch(`${API_URL}/leagues/${leagueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setLeague(data.league);
      else setError(data.message);
    } catch (err) {
      setError('Failed to load league');
    }
  };

  const fetchAvailable = async () => {
    try {
      const res = await fetch(`${API_URL}/draft/${leagueId}/available`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setAvailable(data.players);
    } catch (err) {
      // available will be empty
    }
  };

  const fetchMyPicks = async () => {
    try {
      const res = await fetch(`${API_URL}/draft/${leagueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setMyPicks(data.picks.filter(p => p.manager._id === user?._id));
      }
    } catch (err) {
      // picks will be empty
    }
  };

  const fetchHeroData = async () => {
    try {
      const res = await fetch(`${API_URL}/heroes/${leagueId}/pool`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setHeroPool(data.heroes || []);
        setMyHeroPick(data.myHeroPick || null);
      }
    } catch (err) {
      // hero data optional — silently ignore if heroMode is off
    }
  };

  const handlePickHero = async (playerId) => {
    setHeroLoading(true);
    setHeroError(null);
    try {
      const res = await fetch(`${API_URL}/heroes/${leagueId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (data.success) {
        setMyHeroPick(data.heroPick);
        await fetchHeroData();
      } else {
        setHeroError(data.message);
      }
    } catch (err) {
      setHeroError('Failed to pick hero');
    } finally {
      setHeroLoading(false);
    }
  };

  const handleDraft = async (playerId) => {    setDrafting(playerId);
    setDraftError(null);
    try {
      const res = await fetch(`${API_URL}/draft/${leagueId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (data.success) {
        await Promise.all([fetchAvailable(), fetchMyPicks()]);
      } else {
        setDraftError(data.message);
      }
    } catch (err) {
      setDraftError('Failed to draft player');
    } finally {
      setDrafting(null);
    }
  };

  const handleDrop = async () => {
    if (!dropConfirm) return;
    setDropping(dropConfirm.pickId);
    setDraftError(null);
    try {
      const res = await fetch(`${API_URL}/draft/${leagueId}/my/${dropConfirm.pickId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        await Promise.all([fetchAvailable(), fetchMyPicks()]);
      } else {
        setDraftError(data.message);
      }
    } catch (err) {
      setDraftError('Failed to drop player');
    } finally {
      setDropping(null);
      setDropConfirm(null);
    }
  };

  const sortPlayers = (a, b) => {
    if (b.costCredits !== a.costCredits) return b.costCredits - a.costCredits;
    if (a.ranking !== b.ranking) return a.ranking - b.ranking;
    return a.name.localeCompare(b.name);
  };

  const filtered = available
    .filter((p) => {
      if (filter !== 'ALL' && p.tour !== filter) return false;
      if (nameSearch && !p.name.toLowerCase().includes(nameSearch.toLowerCase())) return false;
      return true;
    })
    .sort(sortPlayers);

  const spent = myPicks.reduce((sum, p) => sum + p.costCredits, 0);
  const budget = league?.draftBudget || 175;
  const remaining = budget - spent;

  const getCostClass = (cost) => {
    if (cost >= 50) return 'cost-high';
    if (cost >= 40) return 'cost-mid';
    return 'cost-low';
  };

  return (
    <div className="draft-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to={`/leagues/${leagueId}`} className="btn btn-secondary">Back to League</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="draft-content">
        <header className="draft-header">
          <h2>🎯 Draft Players</h2>
          {league && <p className="draft-league-name">{league.name}</p>}
        </header>

        {/* Budget Bar */}
        {league && (
          <div className="budget-section">
            <div className="budget-bar-container">
              <div className="budget-bar" style={{ width: `${Math.max(0, (remaining / budget) * 100)}%` }}></div>
            </div>
            <div className="budget-stats">
              <span>💰 {remaining} credits remaining</span>
              <span>{spent} / {budget} spent</span>
              <span>🎯 {myPicks.length} players drafted</span>
            </div>
          </div>
        )}

        {/* My Team */}
        {myPicks.length > 0 && (
          <div className="my-team-section">
            <h3>My Team</h3>
            <table className="my-team-table">
              <thead>
                <tr>
                  <th>Tour</th>
                  <th>Player</th>
                  <th>Cost</th>
                  {league?.draftStatus === 'drafting' && <th></th>}
                </tr>
              </thead>
              <tbody>
                {myPicks.map(pick => (
                  <tr key={pick._id}>
                    <td><span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>{pick.player.tour}</span></td>
                    <td className="player-name"><a href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`} target="_blank" rel="noopener noreferrer">{pick.player.name}</a></td>
                    <td><span className={`cost-badge ${getCostClass(pick.costCredits)}`}>{pick.costCredits} cr</span></td>
                    {league?.draftStatus === 'drafting' && (
                      <td>
                        <button
                          className="btn btn-small btn-drop"
                          disabled={dropping === pick._id}
                          onClick={() => setDropConfirm({
                            pickId: pick._id,
                            playerName: pick.player.name,
                            costCredits: pick.costCredits,
                          })}
                        >
                          {dropping === pick._id ? '...' : 'Drop'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {draftError && <p className="status-msg error">{draftError}</p>}

        {/* Hero pick phase — shown when heroMode is on */}
        {league?.heroMode && (
          <div className="hero-phase-section">
            {myHeroPick ? (
              <div className="hero-picked-banner">
                <span className="hero-icon">⚡</span>
                <div>
                  <strong>Your Hero: {myHeroPick.player?.name}</strong>
                  <span className={`tour-badge ${myHeroPick.player?.tour?.toLowerCase()}`}>{myHeroPick.player?.tour}</span>
                  <span className="hero-free-badge">HERO</span>
                </div>
              </div>
            ) : (
              <>
                <h3 className="hero-phase-title">⚡ Pick Your Hero First</h3>
                <p className="hero-phase-subtitle">Your hero is free, scores normally, and can't be changed. Regular drafting unlocks after you pick.</p>
                {heroError && <p className="status-msg error">{heroError}</p>}
                <div className="hero-pool-grid">
                  {heroPool.map(p => (
                    <div key={p._id} className="hero-card">
                      <span className={`tour-badge ${p.tour.toLowerCase()}`}>{p.tour}</span>
                      <span className="hero-card-name">{p.name}</span>
                      <span className="hero-card-rank">Rank #{p.ranking}</span>
                      <span className="hero-card-count">{p.heroPickCount} {p.heroPickCount === 1 ? 'manager' : 'managers'} picked this hero</span>
                      <button
                        className="btn btn-primary btn-hero-pick"
                        disabled={heroLoading}
                        onClick={() => handlePickHero(p._id)}
                      >
                        {heroLoading ? '…' : '⚡ Pick as Hero'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="filter-bar">
          <button
            className={`filter-btn ${filter === 'ALL' ? 'active' : ''}`}
            onClick={() => setFilter('ALL')}
          >All Players</button>
          {tours.map(t => (
            <button
              key={t.id}
              className={`filter-btn ${filter === t.id ? 'active' : ''}`}
              onClick={() => setFilter(t.id)}
            >{t.label}</button>
          ))}
          <div className="search-input-wrap">
            <input
              type="text"
              placeholder="Search by name..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              className="search-input"
            />
            {nameSearch && (
              <button className="search-clear" onClick={() => setNameSearch('')}>&times;</button>
            )}
          </div>
        </div>

        {loading && <p className="status-msg">Loading...</p>}
        {error && <p className="status-msg error">{error}</p>}

        {!loading && !error && filtered.length === 0 && (
          <p className="status-msg">No available players.</p>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="players-table-wrap">
            <table className="players-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Tour</th>
                  <th>Player</th>
                  <th>Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(player => {
                  const canAfford = player.costCredits <= remaining;
                  const heroOnly = !!player.isHeroOnly;
                  return (
                    <tr key={player._id} className={heroOnly ? 'hero-only-row' : (!canAfford ? 'cant-afford' : '')}>
                      <td className="rank-cell">#{player.ranking}</td>
                      <td><span className={`tour-badge ${player.tour.toLowerCase()}`}>{player.tour}</span></td>
                      <td className="player-name">
                        <a href={`https://google.com/search?q=${encodeURIComponent(player.name)}`} target="_blank" rel="noopener noreferrer">{player.name}</a>
                        {heroOnly && <span className="hero-only-badge"> ⚡ HERO</span>}
                      </td>
                      <td>{heroOnly ? <span className="cost-badge cost-free">HERO</span> : <span className={`cost-badge ${getCostClass(player.costCredits)}`}>{player.costCredits} cr</span>}</td>
                      <td>
                        {!heroOnly && (
                          <button
                            className="btn btn-small btn-primary"
                            disabled={!canAfford || drafting === player._id || league?.draftStatus !== 'drafting' || (league?.heroMode && !myHeroPick)}
                            onClick={() => handleDraft(player._id)}
                          >
                            {drafting === player._id ? '...' : 'Draft'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dropConfirm && (
        <div className="modal-overlay" onClick={() => setDropConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Drop {dropConfirm.playerName}?</h4>
            <p className="drop-confirm-text">
              This will remove them from your team and refund <strong>{dropConfirm.costCredits} credits</strong> to your budget.
              The player will become available for other managers to draft.
            </p>
            <div className="modal-buttons">
              <button className="btn btn-drop-confirm" onClick={handleDrop}>
                Yes, Drop Player
              </button>
              <button className="btn btn-cancel" onClick={() => setDropConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DraftPlayers;
